import { useMutation } from '@tanstack/react-query';
import { getFileUploadUrl, uploadFileToS3 } from '@/shared/lib/file-upload';
import type { ImportFile } from '../model/import-file';

export const MAX_IMPORT_BYTES = 10 * 1024 * 1024;

/** A document is read by the model itself, which takes up to this much. */
export const MAX_DOCUMENT_IMPORT_BYTES = 20 * 1024 * 1024;

export const IMPORT_ACCEPT: Record<string, string[]> = {
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
  'text/csv': ['.csv'],
};

/** What building a schema accepts: a spreadsheet, or a document of naming rules. */
export const SCHEMA_IMPORT_ACCEPT: Record<string, string[]> = {
  ...IMPORT_ACCEPT,
  'application/pdf': ['.pdf'],
};

export const PDF_MIME_TYPE = 'application/pdf';

/** Only these are accepted, so the extension is enough to name the type. */
export function importMimeTypeOf(fileName: string): string {
  const name = fileName.toLowerCase();
  if (name.endsWith('.csv')) return 'text/csv';
  if (name.endsWith('.pdf')) return PDF_MIME_TYPE;
  return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
}

/** Browsers leave `File.type` empty for some files on some systems; the presign needs a real type. */
const mimeTypeOf = (file: File): string => file.type || importMimeTypeOf(file.name);

/** Uploads a naming file to the asset library, for an agent to read by id. */
export function useUploadImportFile() {
  return useMutation({
    mutationFn: async (file: File): Promise<ImportFile> => {
      const mimeType = mimeTypeOf(file);
      const { url, fields, assetId } = await getFileUploadUrl(mimeType, file.name);
      const form = new FormData();
      Object.entries(fields).forEach(([key, value]) => {
        form.append(key, value);
      });
      form.append('file', file);
      await uploadFileToS3(url, form);
      return { assetId, fileName: file.name, mimeType };
    },
  });
}
